import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, BarChart3, Gift, Megaphone, CircleDot, Radio, Crosshair, Trophy, Users, Sparkles, X, User, ScrollText, Star } from "lucide-react";
import api, { fileUrl, LOGO_URL } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { NotifyCTA } from "@/components/NotifyCTA";
import { SpareSignup } from "@/components/SpareSignup";
import { RotatingImage } from "@/components/RotatingImage";
import { PlayerSearch } from "@/components/PlayerSearch";
import { divisionTheme } from "@/lib/divisionTheme";
import { isRichEmpty, RichText, richToPlain } from "@/components/RichText";
import { LiveNowCard } from "@/components/LiveNowCard";
import { SeasonCountdown } from "@/components/SeasonCountdown";

const fmtReviewDate = (d) => {
  if (!d) return "";
  const dt = new Date(d + "T00:00:00");
  return isNaN(dt.getTime()) ? "" : dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export default function Home() {
  const [site, setSite] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [champions, setChampions] = useState([]);
  const [champIdx, setChampIdx] = useState(0);
  const [promoIdx, setPromoIdx] = useState(0);
  const [live, setLive] = useState(null);
  const [spotlight, setSpotlight] = useState(null);
  const [braggers, setBraggers] = useState(null);

  const activePromos = (site?.promos || []).filter((p) => p.active !== false);
  const activePromosCount = activePromos.length;

  useEffect(() => {
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
    api.get("/spotlight").then((r) => setSpotlight(r.data)).catch(() => {});
    api.get("/braggers").then((r) => setBraggers(r.data)).catch(() => {});
    api.get("/divisions").then((r) => {
      const all = r.data || [];
      setDivisions(all.filter((d) => !d.archived));
      const champs = (all.filter((d) => d.archived && d.champion_team && d.champion_image))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      const latestSeason = champs[0]?.season;
      setChampions(champs.filter((c) => c.season === latestSeason));
    }).catch(() => {});
    const checkLive = () => api.get("/live-status").then((r) => setLive(r.data)).catch(() => {});
    checkLive();
    const t = setInterval(checkLive, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (champions.length <= 1) return;
    const t = setInterval(() => setChampIdx((i) => (i + 1) % champions.length), 6000);
    return () => clearInterval(t);
  }, [champions.length]);

  useEffect(() => {
    if (activePromosCount <= 1) return;
    const t = setInterval(() => setPromoIdx((i) => i + 1), 5000);
    return () => clearInterval(t);
  }, [activePromosCount]);

  const champion = champions[champIdx] || null;
  const heroImages = site?.hero_images?.length ? site.hero_images : (site?.hero_image ? [site.hero_image] : []);
  const [promoDismissed, setPromoDismissed] = useState(() => sessionStorage.getItem("qbca_promo_strip") === "1");
  const topPromo = activePromosCount ? activePromos[promoIdx % activePromosCount] : null;
  const dismissPromo = () => {
    sessionStorage.setItem("qbca_promo_strip", "1");
    setPromoDismissed(true);
  };
  const aboutFirstParagraph = richToPlain(site?.about_text || "").split("\n").map((s) => s.trim()).filter(Boolean)[0] || "";

  return (
    <Layout>
      <Seo
        title="Quinte BCA Pool League | Trenton & Quinte, Ontario"
        description="Quinte BCA Pool League offers competitive pool leagues, tournaments, live results, FargoRate stats and billiards events in Trenton and the Quinte region."
        path="/"
        jsonLd={[
          { "@context": "https://schema.org", "@type": "SportsOrganization", name: "Quinte BCA Pool League", url: "https://quintebca.ca", logo: "https://quintebca.ca/quinte-bca-logo.png", description: "Recreational and competitive 8-ball & 9-ball pool leagues and tournaments in Trenton and the Quinte region of Ontario.", areaServed: "Trenton & the Quinte region, Ontario, Canada", sport: "Billiards" },
          { "@context": "https://schema.org", "@type": "WebSite", name: "Quinte BCA Pool League", url: "https://quintebca.ca" },
        ]}
      />
      {/* Dismissible promo strip */}
      {topPromo && !promoDismissed && (
        <div className="bg-[#E11D2A] text-white" data-testid="promo-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[3.5rem] py-2 flex items-center gap-3">
            <Sparkles className="h-4 w-4 shrink-0" />
            <Link to="/promos" className="flex-1 min-w-0 text-sm font-medium hover:underline" data-testid="promo-strip-link">
              <span key={topPromo.id || promoIdx} className="promo-fade line-clamp-2 leading-snug">
                <span className="font-bold">{topPromo.badge || "New"}</span>
                {topPromo.title && <span> — {topPromo.title}</span>}
              </span>
            </Link>
            <Link
              to="/promos"
              data-testid="promo-strip-cta"
              className="shrink-0 hidden sm:inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 rounded-full px-3 py-1 text-xs font-semibold transition-colors"
            >
              See all News/Promos <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <button onClick={dismissPromo} data-testid="promo-strip-dismiss" aria-label="Dismiss" className="shrink-0 text-white/80 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Hero + player search share one background image */}
      <section className="relative" data-testid="hero-section">
        <div className="absolute inset-0">
          {heroImages.length > 0 && (
            <RotatingImage images={heroImages} alt="Pool table" className="h-full w-full" testid="hero-rotating" />
          )}
          <div className="absolute inset-0 bg-[#0B0B0C]/85" />
        </div>
        {braggers && !braggers.hidden && (
        <Link
          to="/braggers"
          data-testid="braggers-cup-link"
          title="Enter the Bragger's Cup"
          className="group absolute top-4 right-4 sm:top-6 sm:right-6 z-20 flex flex-col items-center"
        >
          <img
            src={braggers?.logo || "https://res.cloudinary.com/dlz6knhqm/image/upload/v1782855663/quintebca/uploads/lsasz6ppbhvl9ebepnpu.png"}
            alt="Bragger's Cup"
            className="h-20 sm:h-28 md:h-32 w-auto object-contain drop-shadow-[0_0_25px_rgba(212,175,55,0.45)] transition-transform duration-300 group-hover:scale-105"
          />
          <span className="mt-1 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-[#D4AF37] opacity-90 group-hover:opacity-100">Enter the Cup →</span>
        </Link>
        )}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-16 pb-8">
          <img
            src={LOGO_URL}
            alt="Quinte BCA Pool League logo"
            data-testid="hero-logo"
            className="h-20 sm:h-24 md:h-28 w-auto object-contain mb-5 drop-shadow-[0_0_35px_rgba(30,99,196,0.55)]"
          />
          {live?.is_live && (
            <a
              href={live.watch_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="live-now-badge"
              className="group inline-flex items-center gap-2.5 bg-[#E11D2A] text-white rounded-full pl-3 pr-5 py-2 mb-6 shadow-lg shadow-red-900/40 hover:bg-[#c41320] transition-colors"
            >
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
              </span>
              <span className="font-bold uppercase tracking-wider text-sm">Live now</span>
              <Radio className="h-4 w-4" />
              <span className="hidden sm:inline text-sm font-medium text-white/90 max-w-xs truncate">
                {live.title} — Watch on YouTube
              </span>
            </a>
          )}
          <p className="text-[#E11D2A] font-semibold uppercase tracking-widest text-xs mb-4">Quinte Region • Est. for the love of the game</p>
          <h1 className="font-heading font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none max-w-3xl">
            {site?.hero_title || "Quinte BCA Pool League"}
          </h1>
          <p className="mt-4 text-gray-200 text-lg max-w-2xl leading-relaxed whitespace-pre-line">
            {site?.hero_subtitle}
          </p>
          <div className="mt-6 flex flex-wrap gap-4">
            <Link
              to="/divisions"
              data-testid="hero-divisions-btn"
              className="inline-flex items-center gap-2 bg-[#E11D2A] text-[#111111] hover:bg-[#c41320] transition-colors rounded-md px-6 py-3 font-semibold"
            >
              View Divisions & Standings <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/about"
              data-testid="hero-about-btn"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur text-white border border-white/30 hover:bg-white/20 transition-colors rounded-md px-6 py-3 font-medium"
            >
              About the League
            </Link>
            <SpareSignup />
          </div>
        </div>

        {/* Player / Team look-up — floats over the shared hero background */}
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 md:pb-16" data-testid="home-player-search-section">
          <PlayerSearch className="!mb-0 !bg-white/70 backdrop-blur-xl !border-white/50 !shadow-2xl" />
        </div>
      </section>

      {/* Live tournament banner (only while publicly live) */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 empty:pt-0"><LiveNowCard /></div>
      </section>

      {/* Divisions */}
      <section className="bg-white border-y border-[#E5E7EB]" data-testid="divisions-overview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-[#1E63C4] font-semibold uppercase tracking-widest text-xs mb-2">Play with us</p>
              <h2 className="font-heading text-2xl sm:text-4xl font-bold">Our Divisions</h2>
            </div>
            <Link to="/divisions" className="hidden sm:inline-flex items-center gap-1 text-[#1E63C4] font-semibold hover:gap-2 transition-all">
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {divisions.length === 0 && <p className="text-[#555555]">Divisions will appear here soon.</p>}
            {divisions.map((d) => {
              const theme = divisionTheme(d.color);
              return (
              <div
                key={d.id}
                data-testid={`division-card-${d.slug}`}
                style={{ ...theme.vars, borderTopColor: "var(--div-accent)", borderTopWidth: "3px" }}
                className="group bg-[#F9FAF8] rounded-lg border border-[#E5E7EB] p-6 hover:shadow-lg hover:border-[var(--div-accent)] transition-all flex flex-col"
              >
                <Link to={`/divisions/${d.slug}`} data-testid={`division-card-body-${d.slug}`} className="block">
                  <div className="flex items-center justify-between mb-4">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--div-accent)] text-white">
                      <CircleDot className="h-6 w-6" />
                    </span>
                    {d.week && (
                      <div className="flex flex-wrap items-center justify-end gap-1.5">
                        <SeasonCountdown week={d.week} testid={`home-card-countdown-${d.slug}`} />
                        <span className="text-[11px] font-semibold uppercase tracking-wide bg-[var(--div-tint)] text-[var(--div-accent)] px-2 py-1 rounded-full whitespace-nowrap" data-testid={`home-card-week-${d.slug}`}>Wk {d.week.current} of {d.week.total}</span>
                      </div>
                    )}
                  </div>
                  <h3 className="font-heading font-bold text-xl group-hover:text-[var(--div-accent)] transition-colors">{d.name}</h3>
                  {d.night && <p className="text-sm text-[#E11D2A] font-semibold mt-1">{d.night}</p>}
                  <p className="text-sm text-[#555555] mt-2 leading-relaxed">{d.description}</p>
                  {d.weekly_highlights_enabled !== false && (d.live_stream_teams || (d.show_wanted !== false && d.wanted_player)) && (
                    <div className="mt-3 space-y-1">
                      {d.live_stream_teams && (
                        <p className="text-xs font-semibold text-[#E11D2A] flex items-center gap-1.5" data-testid={`home-live-stream-${d.slug}`}>
                          <Radio className="h-3.5 w-3.5 shrink-0" /> Live Stream: <span className="font-medium text-[#333333]">{d.live_stream_teams}</span>
                        </p>
                      )}
                      {d.show_wanted !== false && d.wanted_player && (
                        <p className="text-xs font-semibold text-amber-700 flex items-center gap-1.5" data-testid={`home-wanted-${d.slug}`}>
                          <Crosshair className="h-3.5 w-3.5 shrink-0" /> WANTED: <span className="font-medium text-[#333333]">{d.wanted_player}</span>
                        </p>
                      )}
                    </div>
                  )}
                </Link>
                {d.review && !isRichEmpty(d.review) && (
                  <Link
                    to={`/divisions/${d.slug}?view=review`}
                    data-testid={`home-review-tag-${d.slug}`}
                    className="mt-3 inline-flex items-center gap-1.5 self-start text-xs font-semibold text-[var(--div-accent)] bg-[var(--div-tint)] border border-[var(--div-border)] rounded-full px-3 py-1 hover:brightness-95 transition-all"
                  >
                    <Star className="h-3.5 w-3.5 shrink-0" /> Review{fmtReviewDate(d.review_date) ? ` · ${fmtReviewDate(d.review_date)}` : ""}
                  </Link>
                )}
                {(() => {
                  const cardBtns = [
                    { label: "Schedule", view: "schedule", key: "schedule", icon: Calendar },
                    { label: "Standings", view: "standings", key: "standings", icon: BarChart3 },
                    { label: "Player Stats", view: "players", key: "players", icon: User },
                    { label: "Team Stats", view: "teams", key: "teams", icon: Users },
                    { label: "Rules", view: "rules", key: "rules", icon: ScrollText },
                  ].filter((b) => (d.card_buttons || {})[b.key] !== false);
                  return (
                    <div className="mt-4 pt-4 border-t border-[#E5E7EB] grid grid-cols-2 gap-2">
                      {cardBtns.length === 0 && (
                        <p className="col-span-2 text-xs text-[#888888] italic text-center py-0.5" data-testid={`home-div-empty-${d.slug}`}>
                          Open this division for standings, stats &amp; schedule.
                        </p>
                      )}
                      {cardBtns.map((b) => (
                        <Link
                          key={b.view}
                          to={`/divisions/${d.slug}?view=${b.view}`}
                          data-testid={`home-div-${b.view}-${d.slug}`}
                          className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-[var(--div-accent)] bg-white border border-[#E5E7EB] rounded-md px-2.5 py-2 hover:bg-[var(--div-accent)] hover:text-white hover:border-[var(--div-accent)] transition-colors"
                        >
                          <b.icon className="h-3.5 w-3.5 shrink-0" /> {b.label}
                        </Link>
                      ))}
                      <Link
                        to={`/divisions/${d.slug}`}
                        data-testid={`home-div-open-${d.slug}`}
                        className={`inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-[#0B0B0C] rounded-md px-2.5 py-2 hover:bg-[var(--div-accent)] transition-colors ${cardBtns.length === 0 ? "col-span-2" : ""}`}
                      >
                        Open division <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  );
                })()}
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Latest champion celebratory banner */}
      {champion && (
        <section className="bg-gradient-to-r from-[#0B0B0C] via-[#13233f] to-[#0B0B0C]" data-testid="home-champion-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12">
            <div className="grid gap-8 md:grid-cols-[1.4fr_1fr] items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#E11D2A] text-white rounded-full px-3 py-1 mb-4">
                  <Trophy className="h-4 w-4" />
                  <span className="text-xs font-bold uppercase tracking-wider">Reigning Champions</span>
                </div>
                <p className="text-[#62A8E5] font-semibold text-sm">
                  {champion.name}{champion.season ? ` · ${champion.season}` : ""}
                </p>
                <h2 className="font-heading text-white font-extrabold text-3xl sm:text-4xl mt-1 whitespace-pre-line leading-tight">{champion.champion_team}</h2>
                {champion.champion_players && (
                  <p className="mt-3 flex items-center gap-2 text-gray-300 text-sm">
                    <Users className="h-4 w-4 text-[#62A8E5] shrink-0" /> {champion.champion_players}
                  </p>
                )}
                <Link
                  to="/champions"
                  data-testid="home-see-champions-btn"
                  className="inline-flex items-center gap-2 mt-6 bg-white text-[#0B0B0C] rounded-md px-5 py-2.5 font-semibold hover:bg-gray-100 transition-colors"
                >
                  See all champions <ArrowRight className="h-4 w-4" />
                </Link>
                {champions.length > 1 && (
                  <div className="flex items-center gap-2 mt-6" data-testid="champion-dots">
                    {champions.map((c, i) => (
                      <button
                        key={c.id}
                        onClick={() => setChampIdx(i)}
                        data-testid={`champion-dot-${i}`}
                        aria-label={`Show champion ${i + 1}`}
                        className={`h-2.5 rounded-full transition-all ${i === champIdx ? "w-7 bg-[#E11D2A]" : "w-2.5 bg-white/30 hover:bg-white/60"}`}
                      />
                    ))}
                  </div>
                )}
              </div>
              {champion.champion_image && (
                <Link key={champion.id} to={`/divisions/${champion.slug}`} className="block rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10 animate-[fadeIn_0.5s_ease]">
                  <img src={fileUrl(champion.champion_image)} alt={`${champion.champion_team} champions`} className="w-full h-auto object-contain bg-black" />
                </Link>
              )}
            </div>
          </div>
        </section>
      )}

      {/* This Week's Spotlight — current division leaders */}
      {spotlight?.enabled && spotlight.leaders?.length > 0 && (
        <section className="bg-[#F9FAF8] border-y border-[#E5E7EB]" data-testid="spotlight-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-5 w-5 text-[#E11D2A]" />
              <p className="text-[#1E63C4] font-semibold uppercase tracking-widest text-xs">This week&apos;s spotlight</p>
            </div>
            <h2 className="font-heading text-2xl sm:text-4xl font-bold mb-8">Division Leaders</h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {spotlight.leaders.map((l) => (
                <Link
                  key={l.slug}
                  to={`/divisions/${l.slug}`}
                  data-testid={`spotlight-card-${l.slug}`}
                  className="group bg-white rounded-lg border border-[#E5E7EB] p-6 hover:shadow-lg hover:border-[#1E63C4] transition-all"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-semibold text-[#E11D2A] uppercase tracking-wide">{l.division_name}</span>
                    <Trophy className="h-5 w-5 text-[#E11D2A]" />
                  </div>
                  <p className="text-[11px] text-[#999999] uppercase tracking-wide">{l.label} leader</p>
                  <h3 className="font-heading font-extrabold text-xl mt-1 group-hover:text-[#1E63C4] transition-colors">{l.leader}</h3>
                  <span className="inline-flex items-center gap-1 text-[#1E63C4] font-semibold text-sm mt-4 group-hover:gap-2 transition-all">
                    View standings <ArrowRight className="h-4 w-4" />
                  </span>
                  {l.last_synced && <p className="mt-3 text-[11px] text-[#999999]">Updated {new Date(l.last_synced).toLocaleDateString()}</p>}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Announcements */}
      {site?.announcements?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid="announcements-section">
          <div className="flex items-center gap-2 mb-6">
            <Megaphone className="h-5 w-5 text-[#1E63C4]" />
            <h2 className="font-heading text-2xl sm:text-3xl font-bold">League News</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {site.announcements.map((a) => (
              <div key={a.id} className="bg-white rounded-lg border border-[#E5E7EB] shadow-sm p-6 hover:shadow-md transition-shadow">
                {a.date && <span className="text-xs font-semibold text-[#E11D2A] uppercase tracking-wide">{a.date}</span>}
                <h3 className="font-heading font-bold text-lg mt-1 mb-2">{a.title}</h3>
                <RichText html={a.body} className="text-sm" testid={`announcement-body-view-${a.id || ""}`} />
              </div>
            ))}
          </div>
        </section>
      )}


      {/* Sponsor ticker */}
      {site?.sponsors?.some((s) => s.image) && (
        <section className="bg-[#0B0B0C] py-8 border-y border-[#1f1f22]" data-testid="sponsor-ticker">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-5">
            <p className="text-center text-[#62A8E5] font-semibold uppercase tracking-widest text-xs">Our proud partners</p>
          </div>
          <div className="marquee-pause overflow-hidden">
            <div className="flex w-max gap-16 animate-marquee items-center">
              {[...site.sponsors.filter((s) => s.image), ...site.sponsors.filter((s) => s.image)].map((s, i) => (
                <Link
                  key={`${s.id}-${i}`}
                  to="/partners"
                  data-testid={`ticker-sponsor-${i}`}
                  className="shrink-0 flex items-center justify-center h-16 opacity-80 hover:opacity-100 transition-opacity"
                  aria-label={s.name}
                >
                  <img src={fileUrl(s.image)} alt={s.name} className="max-h-16 w-auto object-contain" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Feature strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { icon: BarChart3, title: "Live Stats & Standings", text: "Track every division's standings, player win percentages and team rankings, updated weekly." },
            { icon: Calendar, title: "Weekly Schedules", text: "Know exactly who plays who, when and where, all season long." },
            { icon: Gift, title: "Weekly Giveaways", text: site?.giveaway_note || "Qualifying players are entered into our weekly giveaway draws." },
          ].map((f, i) => (
            <div key={i} className="bg-white rounded-lg border border-[#E5E7EB] shadow-sm p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#62A8E5]/15 text-[#1E63C4] mb-4">
                <f.icon className="h-6 w-6" />
              </span>
              <h3 className="font-heading font-bold text-lg mb-2">{f.title}</h3>
              <p className="text-sm text-[#555555] leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* About teaser */}
      {site?.about_text && (
        <section className="bg-[#1E63C4] text-white" data-testid="about-teaser">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 grid gap-10 md:grid-cols-2 items-center">
            <div>
              <h2 className="font-heading text-2xl sm:text-4xl font-bold mb-4">{site.about_title}</h2>
              <p className="text-gray-200 leading-relaxed">{aboutFirstParagraph}</p>
              <Link to="/about" className="inline-flex items-center gap-2 mt-6 bg-[#E11D2A] text-[#111111] rounded-md px-5 py-2.5 font-semibold hover:bg-[#c41320] transition-colors">
                Read more <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            {site.about_image && (
              <img src={fileUrl(site.about_image)} alt="League" className="rounded-lg shadow-xl object-cover w-full h-72" />
            )}
          </div>
        </section>
      )}
      <NotifyCTA />
    </Layout>
  );
}
